import React from 'react';
import { Card, ListGroup, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

/**
 * TopListWidget - Widget para mostrar listas de top items (clientes, productos)
 * @param {string} title - Título del widget
 * @param {Array} items - Array de items a mostrar
 * @param {string} type - 'customers' o 'products'
 * @param {number} maxItems - Cantidad máxima a mostrar (default: 10)
 */
const TopListWidget = ({ title, items = [], type = 'customers', maxItems = 10 }) => {
    const { t, i18n } = useTranslation();

    const formatCurrency = (num) => {
        return '$' + parseFloat(num || 0).toLocaleString(i18n.language, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            style: 'decimal'
        });
    };

    const getMedalIcon = (rank) => {
        switch (rank) {
            case 1: return '🥇';
            case 2: return '🥈';
            case 3: return '🥉';
            default: return `${rank}.`;
        }
    };

    const displayItems = items.slice(0, maxItems);

    if (displayItems.length === 0) {
        return (
            <Card>
                <Card.Body>
                    <Card.Title>{title}</Card.Title>
                    <p className="text-muted text-center py-3">
                        {t('dashboard.noDataAvailable')}
                    </p>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card>
            <Card.Body>
                <Card.Title className="mb-3">{title}</Card.Title>
                <ListGroup variant="flush">
                    {displayItems.map((item, index) => {
                        const rank = item.rank || (index + 1);

                        if (type === 'customers') {
                            return (
                                <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <span className="me-2">{getMedalIcon(rank)}</span>
                                        <strong>{item.client_name}</strong>
                                        {item.purchase_count && (
                                            <Badge bg="secondary" className="ms-2">
                                                {item.purchase_count} {t('dashboard.purchases')}
                                            </Badge>
                                        )}
                                        {item.outstanding_balance > 0 && (
                                            <Badge bg="warning" className="ms-2">
                                                {t('dashboard.pending')}: {formatCurrency(item.outstanding_balance)}
                                            </Badge>
                                        )}
                                    </div>
                                    <strong className="text-success">
                                        {formatCurrency(item.total_spent)}
                                    </strong>
                                </ListGroup.Item>
                            );
                        } else if (type === 'products') {
                            return (
                                <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <span className="me-2">{getMedalIcon(rank)}</span>
                                        <strong>{item.product_name}</strong>
                                        {item.total_quantity && (
                                            <Badge bg="info" className="ms-2">
                                                {parseFloat(item.total_quantity).toFixed(0)} {t('dashboard.units')}
                                            </Badge>
                                        )}
                                    </div>
                                    <strong className="text-success">
                                        {formatCurrency(item.total_revenue)}
                                    </strong>
                                </ListGroup.Item>
                            );
                        }

                        return null;
                    })}
                </ListGroup>
            </Card.Body>
        </Card>
    );
};

export default TopListWidget;
